import { Box, Button, Divider, Stack } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { ChecklistSection } from "Components/Merchants/OrbitLOnboarding/ChecklistSection";
import { RejectionSection } from "Components/Merchants/OrbitLOnboarding/RejectionSection";
import { TerminalsSection } from "Components/Merchants/OrbitLOnboarding/TerminalsSection";
import { TidTypesSection } from "Components/Merchants/OrbitLOnboarding/TidTypesSection";
import { CommentsSection } from "Components/Merchants/OrbitLOnboarding/CommentsSection";
import { SendCommentSection } from "Components/Merchants/OrbitLOnboarding/SendCommentSection";
import { TerminalCountChangeSection } from "Components/Merchants/OrbitLOnboarding/TerminalCountChangeSection";
import { ResubmitConfirmDialog } from "Components/Merchants/OrbitLOnboarding/ResubmitConfirmDialog";
import { useOrbitLOnboardingDetailActions } from "Components/Merchants/OrbitLOnboarding/useOrbitLOnboardingDetailActions";
import type { OrbitLOnboardingDetailDto } from "Infrastructure/Api/Api";

const RESOURCES = Resources.Pages.Content.Merchants.OrbitLOnboarding.Detail;

type Props = {
  detail: OrbitLOnboardingDetailDto;
  merchantPublicID: string;
  isEditingEnabled: boolean;
};

export const OrbitLOnboardingDetailContent = ({
  detail: {
    status: {
      comgateMID,
      comgateStatus,
      checklist,
      rejectionGroups,
      openTerminalCountRequest,
    },
    facilityTerminals,
    contractedTerminalCount,
    contractedSoftPosCount,
    terminalCountHistory,
    tids,
  },
  merchantPublicID,
  isEditingEnabled,
}: Props) => {
  const [commentText, setCommentText] = useState("");
  const [isResubmitDialogOpen, setIsResubmitDialogOpen] = useState(false);

  const { t } = useResource();
  const {
    comments,
    canResubmit,
    isResubmitting,
    isSendingComment,
    isCommentsLoading,
    resubmit,
    sendComment,
  } = useOrbitLOnboardingDetailActions({
    merchantPublicID,
    comgateMID,
    comgateStatus,
    onCommentSent: () => setCommentText(""),
    onResubmitted: () => setIsResubmitDialogOpen(false),
  });

  return (
    <>
      <Stack gap={3}>
        <ChecklistSection checklist={checklist} />
        {!!rejectionGroups?.length && (
          <>
            <Divider />
            <RejectionSection groups={rejectionGroups} />
          </>
        )}
        <Divider />
        <TerminalsSection
          merchantPublicID={merchantPublicID}
          facilityTerminals={facilityTerminals}
        />
        <Divider />
        <TerminalCountChangeSection
          merchantPublicID={merchantPublicID}
          contractedTerminalCount={contractedTerminalCount}
          contractedSoftPosCount={contractedSoftPosCount}
          openRequest={openTerminalCountRequest}
          history={terminalCountHistory}
          isEditingEnabled={isEditingEnabled}
        />
        {tids.length > 0 && (
          <>
            <Divider />
            <TidTypesSection
              merchantPublicID={merchantPublicID}
              tids={tids}
              isEditingEnabled={isEditingEnabled}
            />
          </>
        )}
        {comgateMID && isEditingEnabled && (
          <>
            <Divider />
            <CommentsSection
              comments={comments}
              isLoading={isCommentsLoading}
            />
            <Divider />
            <SendCommentSection
              value={commentText}
              onChange={setCommentText}
              onSend={() => sendComment(commentText)}
              isSending={isSendingComment}
            />
            {canResubmit && (
              <>
                <Divider />
                <Box>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RefreshIcon />}
                    onClick={() => setIsResubmitDialogOpen(true)}
                    disabled={isResubmitting}
                  >
                    {t(RESOURCES.ResubmitButton)}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Stack>
      <ResubmitConfirmDialog
        isOpen={isResubmitDialogOpen}
        isResubmitting={isResubmitting}
        onClose={() => setIsResubmitDialogOpen(false)}
        onConfirm={resubmit}
      />
    </>
  );
};
